import { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Flame, Sun, Moon, Plus, Filter, TrendingUp, Calendar, Edit2, X, Save, Layers, BarChart3, Clock, Eye, ChevronDown, ChevronUp, Package, FileText, Users } from 'lucide-react';

function ProductionReport() {
  const { API } = useContext(AppContext);
  const [productionData, setProductionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [viewMode, setViewMode] = useState('shift'); // 'shift', 'day', 'month', 'year'
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    shift: 'Day',
    scrapType: 'Taldon',
    inputKg: '',
    outputKg: '',
    drossKg: ''
  });
  const [furnaceFilter, setFurnaceFilter] = useState('all'); // 'all', 'big', 'small'

  const scrapTypes = ['Taldon', 'Telic', 'Twitch', 'Talc', 'Tally', 'Taint Tabor'];

  // Furnace classification: Big = C1-C15, Small = SM 1-SM 7
  const isBigFurnace = (furnace) => {
    if (!furnace) return false;
    const f = furnace.trim().toUpperCase();
    return /^C\d{1,2}$/.test(f);
  };
  const isSmallFurnace = (furnace) => {
    if (!furnace) return false;
    const f = furnace.trim().toUpperCase();
    return /^SM\s?\d{1,2}$/.test(f);
  };

  // Apply furnace filter to production data
  const filteredData = useMemo(() => {
    if (furnaceFilter === 'all') return productionData;
    return productionData.filter(record => {
      const furnace = record.furnace || '';
      if (furnaceFilter === 'big') return isBigFurnace(furnace);
      if (furnaceFilter === 'small') return isSmallFurnace(furnace);
      return true;
    });
  }, [productionData, furnaceFilter]);

  // Function to fetch detailed shift data
  const fetchShiftDetail = async (date, shift) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/production/shift-data-detailed`, {
        params: { date, shift }
      });
      setDetailData(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching shift detail:', error);
    }
    setDetailLoading(false);
  };

  useEffect(() => {
    fetchProductionData();
  }, [selectedMonth, selectedYear, viewMode]);

  const fetchProductionData = async () => {
    setLoading(true);
    try {
      const params = viewMode === 'year' 
        ? { year: selectedYear } 
        : { month: selectedMonth };
      
      const response = await axios.get(`${API}/production/shift-data`, {
        params,
        
      });
      setProductionData(response.data || []);
    } catch (error) {
      console.error('Error fetching production data:', error);
      // Return empty array on error - no mock data
      setProductionData([]);
    }
    setLoading(false);
  };

  const calculateDerivedValues = (input, output, dross) => {
    const burningLoss = input - output - dross;
    const recoveryPercent = input > 0 ? ((output / input) * 100).toFixed(1) : 0;
    const drossPercent = input > 0 ? ((dross / input) * 100).toFixed(1) : 0;
    const burningLossPercent = input > 0 ? ((burningLoss / input) * 100).toFixed(1) : 0;
    return { burningLoss, recoveryPercent, drossPercent, burningLossPercent };
  };

  const handleAddProduction = async (e) => {
    e.preventDefault();
    const input = parseFloat(formData.inputKg);
    const output = parseFloat(formData.outputKg);
    const dross = parseFloat(formData.drossKg);
    
    const newRecord = {
      id: `PROD-${formData.date}-${formData.shift.toUpperCase()}`,
      date: formData.date,
      shift: formData.shift,
      scrapType: formData.scrapType,
      inputKg: input,
      outputKg: output,
      drossKg: dross,
    };

    const existingIndex = productionData.findIndex(
      p => p.date === formData.date && p.shift === formData.shift
    );

    if (existingIndex >= 0) {
      const updated = [...productionData];
      updated[existingIndex] = newRecord;
      setProductionData(updated);
    } else {
      setProductionData([...productionData, newRecord]);
    }

    setShowAddModal(false);
    setFormData({ date: new Date().toISOString().split('T')[0], shift: 'Day', scrapType: 'Taldon', inputKg: '', outputKg: '', drossKg: '' });
  };

  // Group data by date for shift view - SUM all furnace entries
  const groupedByDate = useMemo(() => {
    const result = {};
    filteredData.forEach(record => {
      if (!result[record.date]) {
        result[record.date] = { 
          day: { inputKg: 0, outputKg: 0, drossKg: 0, burningLossKg: 0, scrapType: 'Mixed', count: 0 }, 
          night: { inputKg: 0, outputKg: 0, drossKg: 0, burningLossKg: 0, scrapType: 'Mixed', count: 0 } 
        };
      }
      const shiftKey = record.shift === 'Day' ? 'day' : 'night';
      result[record.date][shiftKey].inputKg += (record.inputKg || 0);
      result[record.date][shiftKey].outputKg += (record.outputKg || 0);
      result[record.date][shiftKey].drossKg += (record.drossKg || 0);
      result[record.date][shiftKey].burningLossKg += (record.burningLossKg || 0);
      result[record.date][shiftKey].count++;
      // Keep first non-Mixed scrap type
      if (record.scrapType && record.scrapType !== 'Mixed') {
        result[record.date][shiftKey].scrapType = record.scrapType;
      }
    });
    // Set null for shifts with no data (count = 0)
    Object.keys(result).forEach(date => {
      if (result[date].day.count === 0) result[date].day = null;
      if (result[date].night.count === 0) result[date].night = null;
    });
    return result;
  }, [filteredData]);

  // Aggregate data by day
  const dailyData = useMemo(() => {
    const daily = {};
    filteredData.forEach(p => {
      if (!daily[p.date]) {
        daily[p.date] = { date: p.date, inputKg: 0, outputKg: 0, drossKg: 0, shifts: 0 };
      }
      daily[p.date].inputKg += p.inputKg;
      daily[p.date].outputKg += p.outputKg;
      daily[p.date].drossKg += p.drossKg;
      daily[p.date].shifts++;
    });
    return Object.values(daily).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [filteredData]);

  // Aggregate data by month
  const monthlyData = useMemo(() => {
    const monthly = {};
    filteredData.forEach(p => {
      const monthKey = p.date.slice(0, 7); // YYYY-MM
      if (!monthly[monthKey]) {
        monthly[monthKey] = { month: monthKey, inputKg: 0, outputKg: 0, drossKg: 0, shifts: 0, days: new Set() };
      }
      monthly[monthKey].inputKg += p.inputKg;
      monthly[monthKey].outputKg += p.outputKg;
      monthly[monthKey].drossKg += p.drossKg;
      monthly[monthKey].shifts++;
      monthly[monthKey].days.add(p.date);
    });
    return Object.values(monthly)
      .map(m => ({ ...m, days: m.days.size }))
      .sort((a, b) => b.month.localeCompare(a.month));
  }, [filteredData]);

  // Aggregate data by year
  const yearlyData = useMemo(() => {
    const yearly = {};
    filteredData.forEach(p => {
      const yearKey = p.date.slice(0, 4); // YYYY
      if (!yearly[yearKey]) {
        yearly[yearKey] = { year: yearKey, inputKg: 0, outputKg: 0, drossKg: 0, shifts: 0, days: new Set(), months: new Set() };
      }
      yearly[yearKey].inputKg += p.inputKg;
      yearly[yearKey].outputKg += p.outputKg;
      yearly[yearKey].drossKg += p.drossKg;
      yearly[yearKey].shifts++;
      yearly[yearKey].days.add(p.date);
      yearly[yearKey].months.add(p.date.slice(0, 7));
    });
    return Object.values(yearly)
      .map(y => ({ ...y, days: y.days.size, months: y.months.size }))
      .sort((a, b) => b.year.localeCompare(a.year));
  }, [filteredData]);

  // Calculate summary stats
  const totalInput = filteredData.reduce((sum, p) => sum + p.inputKg, 0);
  const totalOutput = filteredData.reduce((sum, p) => sum + p.outputKg, 0);
  const totalDross = filteredData.reduce((sum, p) => sum + p.drossKg, 0);
  const totalBurningLoss = totalInput - totalOutput - totalDross;
  const avgRecovery = totalInput > 0 ? ((totalOutput / totalInput) * 100).toFixed(1) : 0;

  const dayShiftData = filteredData.filter(p => p.shift === 'Day');
  const nightShiftData = filteredData.filter(p => p.shift === 'Night');
  
  const dayTotals = {
    input: dayShiftData.reduce((s, p) => s + p.inputKg, 0),
    output: dayShiftData.reduce((s, p) => s + p.outputKg, 0),
    dross: dayShiftData.reduce((s, p) => s + p.drossKg, 0),
  };
  const nightTotals = {
    input: nightShiftData.reduce((s, p) => s + p.inputKg, 0),
    output: nightShiftData.reduce((s, p) => s + p.outputKg, 0),
    dross: nightShiftData.reduce((s, p) => s + p.drossKg, 0),
  };

  const viewModeLabels = {
    shift: 'Shift-wise',
    day: 'Day-wise',
    month: 'Month-wise',
    year: 'Year-wise'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="production-report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Flame className="text-orange-500" size={28} />
            Production Report - {viewModeLabels[viewMode]}
          </h1>
          <p className="text-gray-600 mt-1">Track production data by shift, day, month, or year</p>
        </div>
        {/* Add button removed per user request */}
      </div>

      {/* View Mode Toggle & Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        {/* View Mode Toggle */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700 mr-2">View By:</span>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('shift')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition flex items-center gap-1.5 ${
                viewMode === 'shift' 
                  ? 'bg-white shadow text-orange-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="view-shift-btn"
            >
              <Clock size={14} />
              Shift
            </button>
            <button
              onClick={() => setViewMode('day')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition flex items-center gap-1.5 ${
                viewMode === 'day' 
                  ? 'bg-white shadow text-orange-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="view-day-btn"
            >
              <Sun size={14} />
              Day
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition flex items-center gap-1.5 ${
                viewMode === 'month' 
                  ? 'bg-white shadow text-orange-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="view-month-btn"
            >
              <Calendar size={14} />
              Month
            </button>
            <button
              onClick={() => setViewMode('year')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition flex items-center gap-1.5 ${
                viewMode === 'year' 
                  ? 'bg-white shadow text-orange-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="view-year-btn"
            >
              <Layers size={14} />
              Year
            </button>
          </div>
        </div>

        {/* Date Filters & Furnace Filter */}
        <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-gray-100">
          {(viewMode === 'shift' || viewMode === 'day') && (
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-gray-400" />
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                data-testid="month-filter"
              />
            </div>
          )}
          {(viewMode === 'month' || viewMode === 'year') && (
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-gray-400" />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                data-testid="year-filter"
              >
                {[2024, 2025, 2026, 2027].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}

          {/* Furnace Size Filter */}
          <div className="flex items-center gap-2 ml-auto">
            <Filter size={16} className="text-gray-400" />
            <span className="text-xs font-medium text-gray-500">Furnace:</span>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setFurnaceFilter('all')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                  furnaceFilter === 'all'
                    ? 'bg-white shadow text-orange-600 border border-orange-200'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="furnace-filter-all"
              >
                All
              </button>
              <button
                onClick={() => setFurnaceFilter('big')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                  furnaceFilter === 'big'
                    ? 'bg-white shadow text-blue-600 border border-blue-200'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="furnace-filter-big"
              >
                Big (C1-C15)
              </button>
              <button
                onClick={() => setFurnaceFilter('small')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                  furnaceFilter === 'small'
                    ? 'bg-white shadow text-purple-600 border border-purple-200'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="furnace-filter-small"
              >
                Small (SM1-SM7)
              </button>
            </div>
          </div>

          <span className="text-sm text-gray-600">
            Showing {filteredData.length} shift records{furnaceFilter !== 'all' ? ` (${furnaceFilter === 'big' ? 'Big Furnaces' : 'Small Furnaces'})` : ''}
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Total Input</p>
          <p className="text-2xl font-bold">{(totalInput / 1000).toFixed(1)} T</p>
          <p className="text-xs opacity-70 mt-1">{filteredData.length} shifts</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Total Output</p>
          <p className="text-2xl font-bold">{(totalOutput / 1000).toFixed(1)} T</p>
          <p className="text-xs opacity-70 mt-1">Finished Goods</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Total Dross</p>
          <p className="text-2xl font-bold">{(totalDross / 1000).toFixed(1)} T</p>
          <p className="text-xs opacity-70 mt-1">{totalInput > 0 ? ((totalDross / totalInput) * 100).toFixed(1) : 0}%</p>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Burning Loss</p>
          <p className="text-2xl font-bold">{(totalBurningLoss / 1000).toFixed(1)} T</p>
          <p className="text-xs opacity-70 mt-1">{totalInput > 0 ? ((totalBurningLoss / totalInput) * 100).toFixed(1) : 0}%</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Avg Recovery</p>
          <p className="text-2xl font-bold">{avgRecovery}%</p>
          <p className="text-xs opacity-70 mt-1">
            <TrendingUp size={12} className="inline" /> Performance
          </p>
        </div>
      </div>

      {/* Shift-wise Summary (only for shift view) */}
      {viewMode === 'shift' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
            <div className="flex items-center gap-2 mb-4">
              <Sun className="text-amber-500" size={24} />
              <h3 className="font-bold text-gray-900">Day Shift Summary (6AM - 6PM)</h3>
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Shifts</p>
                <p className="font-bold text-lg text-gray-900">{dayShiftData.length}</p>
              </div>
              <div>
                <p className="text-gray-500">Input</p>
                <p className="font-bold text-lg text-gray-900">{(dayTotals.input / 1000).toFixed(1)} T</p>
              </div>
              <div>
                <p className="text-gray-500">Output</p>
                <p className="font-bold text-lg text-green-600">{(dayTotals.output / 1000).toFixed(1)} T</p>
              </div>
              <div>
                <p className="text-gray-500">Recovery</p>
                <p className="font-bold text-lg text-amber-600">
                  {dayTotals.input > 0 ? ((dayTotals.output / dayTotals.input) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-200">
            <div className="flex items-center gap-2 mb-4">
              <Moon className="text-indigo-500" size={24} />
              <h3 className="font-bold text-gray-900">Night Shift Summary (6PM - 6AM)</h3>
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Shifts</p>
                <p className="font-bold text-lg text-gray-900">{nightShiftData.length}</p>
              </div>
              <div>
                <p className="text-gray-500">Input</p>
                <p className="font-bold text-lg text-gray-900">{(nightTotals.input / 1000).toFixed(1)} T</p>
              </div>
              <div>
                <p className="text-gray-500">Output</p>
                <p className="font-bold text-lg text-green-600">{(nightTotals.output / 1000).toFixed(1)} T</p>
              </div>
              <div>
                <p className="text-gray-500">Recovery</p>
                <p className="font-bold text-lg text-indigo-600">
                  {nightTotals.input > 0 ? ((nightTotals.output / nightTotals.input) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Tables based on View Mode */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Flame size={18} className="text-orange-500" />
            {viewModeLabels[viewMode]} Production Log
          </h3>
        </div>

        {/* SHIFT VIEW TABLE */}
        {viewMode === 'shift' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Shift</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Input (kg)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Output (kg)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Dross (kg)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">B. Loss (kg)</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Recovery %</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(groupedByDate)
                  .sort(([a], [b]) => new Date(b) - new Date(a))
                  .map(([date, shifts]) => (
                    <>
                      {shifts.day && (
                        <tr key={`${date}-day`} className="hover:bg-amber-50/50 transition">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                              <Sun size={12} /> Day
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{shifts.day.inputKg.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-green-600">{shifts.day.outputKg.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right text-yellow-600">{shifts.day.drossKg.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right text-red-600">
                            {calculateDerivedValues(shifts.day.inputKg, shifts.day.outputKg, shifts.day.drossKg).burningLoss.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                              parseFloat(calculateDerivedValues(shifts.day.inputKg, shifts.day.outputKg, shifts.day.drossKg).recoveryPercent) >= 85 
                                ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {calculateDerivedValues(shifts.day.inputKg, shifts.day.outputKg, shifts.day.drossKg).recoveryPercent}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => fetchShiftDetail(date, 'Day')}
                              className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition"
                              title="View Shift Details"
                            >
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                      )}
                      {shifts.night && (
                        <tr key={`${date}-night`} className="hover:bg-indigo-50/50 transition">
                          <td className="px-4 py-3 text-sm text-gray-500">{!shifts.day && new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200">
                              <Moon size={12} /> Night
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{shifts.night.inputKg.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-green-600">{shifts.night.outputKg.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right text-yellow-600">{shifts.night.drossKg.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right text-red-600">
                            {calculateDerivedValues(shifts.night.inputKg, shifts.night.outputKg, shifts.night.drossKg).burningLoss.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                              parseFloat(calculateDerivedValues(shifts.night.inputKg, shifts.night.outputKg, shifts.night.drossKg).recoveryPercent) >= 85 
                                ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {calculateDerivedValues(shifts.night.inputKg, shifts.night.outputKg, shifts.night.drossKg).recoveryPercent}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => fetchShiftDetail(date, 'Night')}
                              className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition"
                              title="View Shift Details"
                            >
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {/* DAY VIEW TABLE */}
        {viewMode === 'day' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Shifts</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Input (kg)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Output (kg)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Dross (kg)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Burning Loss (kg)</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Recovery %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dailyData.map(day => {
                  const derived = calculateDerivedValues(day.inputKg, day.outputKg, day.drossKg);
                  return (
                    <tr key={day.date} className="hover:bg-orange-50/50 transition">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {new Date(day.date).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{day.shifts}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{day.inputKg.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600">{day.outputKg.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right text-yellow-600">{day.drossKg.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right text-red-600">{derived.burningLoss.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          parseFloat(derived.recoveryPercent) >= 85 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {derived.recoveryPercent}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-orange-50 border-t-2 border-orange-200">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-800">TOTAL</td>
                  <td className="px-4 py-3 text-center font-bold text-gray-800">{filteredData.length}</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-800">{totalInput.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-bold text-green-700">{totalOutput.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-bold text-yellow-700">{totalDross.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-bold text-red-700">{totalBurningLoss.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center font-bold text-purple-700">{avgRecovery}%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* MONTH VIEW TABLE */}
        {viewMode === 'month' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Month</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Working Days</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Shifts</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Input (T)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Output (T)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Dross (T)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Burning Loss (T)</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Recovery %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {monthlyData.map(m => {
                  const derived = calculateDerivedValues(m.inputKg, m.outputKg, m.drossKg);
                  return (
                    <tr key={m.month} className="hover:bg-orange-50/50 transition">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {new Date(m.month + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">{m.days}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{m.shifts}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{(m.inputKg / 1000).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600">{(m.outputKg / 1000).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right text-yellow-600">{(m.drossKg / 1000).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right text-red-600">{(derived.burningLoss / 1000).toFixed(2)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          parseFloat(derived.recoveryPercent) >= 85 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {derived.recoveryPercent}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-orange-50 border-t-2 border-orange-200">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-800">YEAR TOTAL</td>
                  <td className="px-4 py-3 text-center font-bold text-gray-800">{dailyData.length}</td>
                  <td className="px-4 py-3 text-center font-bold text-gray-800">{filteredData.length}</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-800">{(totalInput / 1000).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-bold text-green-700">{(totalOutput / 1000).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-bold text-yellow-700">{(totalDross / 1000).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-bold text-red-700">{(totalBurningLoss / 1000).toFixed(2)}</td>
                  <td className="px-4 py-3 text-center font-bold text-purple-700">{avgRecovery}%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* YEAR VIEW TABLE */}
        {viewMode === 'year' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Year</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Months</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Working Days</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Shifts</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Input (T)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Output (T)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Dross (T)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Burning Loss (T)</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Avg Recovery %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {yearlyData.map(y => {
                  const derived = calculateDerivedValues(y.inputKg, y.outputKg, y.drossKg);
                  return (
                    <tr key={y.year} className="hover:bg-orange-50/50 transition">
                      <td className="px-4 py-3 text-lg font-bold text-gray-900">{y.year}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">{y.months}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">{y.days}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{y.shifts}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{(y.inputKg / 1000).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600">{(y.outputKg / 1000).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right text-yellow-600">{(y.drossKg / 1000).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right text-red-600">{(derived.burningLoss / 1000).toFixed(2)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-3 py-1.5 rounded-full text-sm font-bold ${
                          parseFloat(derived.recoveryPercent) >= 85 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {derived.recoveryPercent}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {filteredData.length === 0 && (
          <div className="text-center py-12">
            <Flame className="mx-auto text-gray-400 mb-3" size={48} />
            <p className="text-gray-500">No production data available</p>
          </div>
        )}
      </div>

      {/* Add Shift Data Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-2xl">
              <h2 className="text-xl font-bold">Add Shift Production Data</h2>
              <p className="text-sm opacity-80 mt-1">Enter total production for one shift</p>
            </div>
            <form onSubmit={handleAddProduction} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shift *</label>
                  <select
                    required
                    value={formData.shift}
                    onChange={(e) => setFormData({...formData, shift: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  >
                    <option value="Day">Day Shift (6AM - 6PM)</option>
                    <option value="Night">Night Shift (6PM - 6AM)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Scrap Type *</label>
                <select
                  required
                  value={formData.scrapType}
                  onChange={(e) => setFormData({...formData, scrapType: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                >
                  {scrapTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-3">Shift Totals</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Input (kg) *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={formData.inputKg}
                      onChange={(e) => setFormData({...formData, inputKg: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                      placeholder="5000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Output (kg) *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={formData.outputKg}
                      onChange={(e) => setFormData({...formData, outputKg: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                      placeholder="4200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Dross (kg) *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={formData.drossKg}
                      onChange={(e) => setFormData({...formData, drossKg: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                      placeholder="500"
                    />
                  </div>
                </div>
              </div>

              {formData.inputKg && formData.outputKg && formData.drossKg && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-blue-700 font-medium mb-2">Calculated Values:</p>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500">Burning Loss</p>
                      <p className="font-bold text-red-600">
                        {(parseFloat(formData.inputKg) - parseFloat(formData.outputKg) - parseFloat(formData.drossKg)).toLocaleString()} kg
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Recovery</p>
                      <p className="font-bold text-green-600">
                        {((parseFloat(formData.outputKg) / parseFloat(formData.inputKg)) * 100).toFixed(1)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Dross %</p>
                      <p className="font-bold text-yellow-600">
                        {((parseFloat(formData.drossKg) / parseFloat(formData.inputKg)) * 100).toFixed(1)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">B. Loss %</p>
                      <p className="font-bold text-red-600">
                        {(((parseFloat(formData.inputKg) - parseFloat(formData.outputKg) - parseFloat(formData.drossKg)) / parseFloat(formData.inputKg)) * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 flex items-center gap-2"
                >
                  <Save size={16} />
                  Save Shift Data
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shift Detail Modal */}
      {showDetailModal && detailData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="shift-detail-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1a237e] to-[#283593] p-4 text-white">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <FileText size={24} />
                  <div>
                    <h2 className="text-xl font-bold">Shift Production Report</h2>
                    <p className="text-sm opacity-80">
                      {new Date(detailData.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })} - {detailData.shift} Shift
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-white/20 rounded-lg transition">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                  <p className="text-xs text-blue-600 font-medium">Total Input</p>
                  <p className="text-lg font-bold text-blue-800">{(detailData.summary.totalInput / 1000).toFixed(2)} T</p>
                  <p className="text-xs text-blue-500">{detailData.summary.totalInput.toLocaleString()} kg</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                  <p className="text-xs text-green-600 font-medium">Total Output</p>
                  <p className="text-lg font-bold text-green-800">{(detailData.summary.totalOutput / 1000).toFixed(2)} T</p>
                  <p className="text-xs text-green-500">{detailData.summary.totalOutput.toLocaleString()} kg</p>
                </div>
                <div className="bg-yellow-50 rounded-xl p-3 border border-yellow-200">
                  <p className="text-xs text-yellow-600 font-medium">Total Dross</p>
                  <p className="text-lg font-bold text-yellow-800">{detailData.summary.totalDross.toLocaleString()} kg</p>
                </div>
                <div className="bg-red-50 rounded-xl p-3 border border-red-200">
                  <p className="text-xs text-red-600 font-medium">Burning Loss</p>
                  <p className="text-lg font-bold text-red-800">{detailData.summary.totalBurningLoss.toLocaleString()} kg</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-3 border border-purple-200">
                  <p className="text-xs text-purple-600 font-medium">Recovery %</p>
                  <p className="text-lg font-bold text-purple-800">{detailData.summary.overallRecovery}%</p>
                  <p className="text-xs text-purple-500">{detailData.summary.furnaceCount} furnaces</p>
                </div>
              </div>

              {/* Party-wise Summary */}
              {detailData.partySummary && detailData.partySummary.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Users size={16} /> Party-wise Input Summary
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {detailData.partySummary.map((party, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-sm">
                        <span className="font-medium">{party.party}:</span> {party.weight.toLocaleString()} kg
                        <span className="text-gray-500 ml-1">({party.count} lots)</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Scrap Summary */}
              {detailData.scrapSummary && detailData.scrapSummary.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Package size={16} /> Scrap Type Summary
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {detailData.scrapSummary.map((scrap, idx) => (
                      <span key={idx} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                        <span className="font-medium">{scrap.type}:</span> {scrap.weight.toLocaleString()} kg
                        <span className="text-gray-500 ml-1">({scrap.count} lots)</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Furnace Details */}
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Flame size={16} /> Furnace-wise Details
              </h3>
              <div className="space-y-4">
                {detailData.furnaceDetails.filter(f => f.totalInput > 0 || f.totalOutput > 0).map((furnace, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-bold text-[#1a237e]">Furnace {furnace.furnace}</h4>
                        <p className="text-xs text-gray-500">Batch: {furnace.batchNumber}</p>
                        {furnace.operatorNames && <p className="text-xs text-gray-500">Operator: {furnace.operatorNames}</p>}
                        {furnace.contractor && <p className="text-xs text-gray-500">Contractor: {furnace.contractor}</p>}
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          furnace.recoveryPercent >= 85 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {furnace.recoveryPercent.toFixed(1)}% Recovery
                        </span>
                        <p className="text-xs text-gray-500 mt-1">FG: {furnace.fgType}</p>
                      </div>
                    </div>

                    {/* Furnace Metrics */}
                    <div className="grid grid-cols-5 gap-2 mb-3 text-center text-xs">
                      <div className="bg-white rounded-lg p-2">
                        <p className="text-gray-500">Input</p>
                        <p className="font-bold text-gray-800">{furnace.totalInput.toLocaleString()} kg</p>
                      </div>
                      <div className="bg-white rounded-lg p-2">
                        <p className="text-gray-500">Output</p>
                        <p className="font-bold text-green-600">{furnace.totalOutput.toLocaleString()} kg</p>
                      </div>
                      <div className="bg-white rounded-lg p-2">
                        <p className="text-gray-500">Dross</p>
                        <p className="font-bold text-yellow-600">{furnace.dross.toLocaleString()} kg ({furnace.drossPercent.toFixed(1)}%)</p>
                      </div>
                      <div className="bg-white rounded-lg p-2">
                        <p className="text-gray-500">B. Loss</p>
                        <p className="font-bold text-red-600">{furnace.burningLoss.toLocaleString()} kg ({furnace.burningLossPercent.toFixed(1)}%)</p>
                      </div>
                      <div className="bg-white rounded-lg p-2">
                        <p className="text-gray-500">Status</p>
                        <p className={`font-bold ${furnace.status === 'Closed' ? 'text-green-600' : 'text-orange-600'}`}>{furnace.status}</p>
                      </div>
                    </div>

                    {/* Lot Details */}
                    {furnace.lotDetails && furnace.lotDetails.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Lot Weighment Breakup:</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-2 py-1 text-left">Invoice No</th>
                                <th className="px-2 py-1 text-left">Party Name</th>
                                <th className="px-2 py-1 text-left">Scrap Type</th>
                                <th className="px-2 py-1 text-right">Weight (kg)</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {furnace.lotDetails.map((lot, lotIdx) => (
                                <tr key={lotIdx} className="hover:bg-gray-50">
                                  <td className="px-2 py-1 font-mono">{lot.invoiceNo || '-'}</td>
                                  <td className="px-2 py-1">{lot.partyName || '-'}</td>
                                  <td className="px-2 py-1">
                                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{lot.scrapType}</span>
                                  </td>
                                  <td className="px-2 py-1 text-right font-medium">{lot.weight.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {furnace.remarks && (
                      <p className="text-xs text-gray-500 mt-2 italic">Remarks: {furnace.remarks}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductionReport;
